import clsx from 'clsx'
import { useI18n } from '@/i18n/I18nProvider.tsx'
import { LOCALES, type Locale } from '@/i18n/locale.ts'

/**
 * The one control in this product that has to be comprehensible to somebody who cannot read
 * the language currently on screen.
 *
 * Which is why each option is labelled in ITS OWN language and carries its own `lang`
 * attribute (PRD-27 LOC-065): a screen reader then announces "Português" with Portuguese
 * phonemes instead of sounding it out in English, and a Brazilian looking at an English
 * interface recognises the word they are looking for. Labelling the options in the CURRENT
 * language — "Portuguese" / "English" — is the obvious mistake, and it defeats the one job
 * the control has.
 *
 * Buttons rather than a `<select>`: two options, one tap, current state visible without
 * opening anything, and `aria-pressed` carrying that state rather than colour alone.
 *
 * It appears twice, because `/login` sits outside `AppShell` (F-23): in the workspace menu
 * beside the theme toggle, and in the sign-in panel's footer. A person must be able to
 * change language BEFORE they have an account whose preference could be read.
 */

const SHORT_LABEL: Record<Locale, string> = { en: 'EN', 'pt-BR': 'PT' }

export const LanguageSwitcher = ({ className }: { className?: string }) => {
  const { locale, setLocale, t } = useI18n()
  const nameOf = (value: Locale): string => (value === 'en' ? t.common.languageNameEn : t.common.languageNamePt)

  return (
    <fieldset
      aria-label={t.common.language}
      className={clsx('inline-flex min-w-0 items-center gap-0.5 rounded-full border border-rule p-0.5', className)}
    >
      {LOCALES.map((value) => (
        <button
          key={value}
          type="button"
          lang={value}
          aria-pressed={value === locale}
          title={nameOf(value)}
          onClick={() => setLocale(value)}
          className={clsx(
            'rounded-full px-2 py-0.5 text-[11px] font-medium tracking-[0.02em]',
            'transition-[color,background-color] duration-200',
            value === locale ? 'bg-card-solid text-fg shadow-glass' : 'text-fg-subtle hover:text-fg'
          )}
        >
          {SHORT_LABEL[value]}
        </button>
      ))}
    </fieldset>
  )
}
